import { useRef, type ButtonHTMLAttributes } from "react";

/** One gesture owner for the button group. Preserve pointerdown's button even
 * when the browser delivers its later click to another button. Activate at
 * click time so that click cannot fall through into the newly opened form.
 */
export function usePressGroup(
  onPress: (pressIndex: number) => void,
  disabled: boolean,
) {
  const press = useRef<{
    id: number;
    x: number;
    y: number;
    moved: boolean;
    released: boolean;
    pressIndex: number;
  } | null>(null);
  const props: ButtonHTMLAttributes<HTMLButtonElement> = {
    onPointerDown(e) {
      if (disabled || !e.isPrimary || e.button !== 0) return;
      press.current = {
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        moved: false,
        released: false,
        pressIndex: Number(e.currentTarget.dataset.pressIndex),
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    onPointerMove(e) {
      const p = press.current;
      if (
        p?.id === e.pointerId &&
        Math.hypot(e.clientX - p.x, e.clientY - p.y) > 12
      )
        p.moved = true;
    },
    onPointerUp(e) {
      const p = press.current;
      if (p?.id !== e.pointerId) return;
      if (
        disabled ||
        p.moved ||
        Math.hypot(e.clientX - p.x, e.clientY - p.y) > 12
      )
        press.current = null;
      else p.released = true;
    },
    onPointerCancel() {
      press.current = null;
    },
    onLostPointerCapture() {
      // Normal release loses capture before click; retain its identity.
      if (!press.current?.released) press.current = null;
    },
    onContextMenu() {
      press.current = null;
    },
    onClick(e) {
      const p = press.current;
      press.current = null;
      e.preventDefault();
      if (disabled) return;
      // Keyboard and assistive clicks have no pointer sequence.
      if (e.detail === 0) onPress(Number(e.currentTarget.dataset.pressIndex));
      else if (p?.released && !p.moved) onPress(p.pressIndex);
    },
  };
  return props;
}
